import React, { useEffect, useMemo, useState } from "react";
import TransactionTable from "./UserDeposit/TransactionTable";
import PaymentMethodService from "../../service/PaymentMethodService";
import WithdrawalService from "../../service/WithdrawalService";
import { useAuth } from "provider/AuthContext";

function UserWithdrawal() {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [transactions, setTransactions] = useState([]); // real data from API
  const [selectedMethod, setSelectedMethod] = useState("");
  const [amount, setAmount] = useState("");
  const [accountDetails, setAccountDetails] = useState("");
  const [error, setError] = useState("");

  const paymentMethodService = new PaymentMethodService();
  const withdrawalService = new WithdrawalService();
  const { user } = useAuth();

  const fetchPaymentMethods = async () => {
    if (paymentMethods.length > 0) return; // only fetch once
    try {
      setLoading(true);
      setErrorMessage("");
      // we want only active methods, and only those that support withdrawal
      const methods = await paymentMethodService.fetchPaymentMethods({ activeOnly: true });
      // filter to withdrawal or both (your schema)
      const withdrawable = (methods || []).filter(
        (m) => m.isActive && (m.type === "withdrawal" || m.type === "both")
      );
      setPaymentMethods(withdrawable);
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      setErrorMessage(error.message || "Failed to fetch payment methods");
    } finally {
      setLoading(false);
    }
  };

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const res = await withdrawalService.fetchUserWithdrawals({ items_per_page: 100 });
      // normalize to array
      const rows = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.items)
          ? res.data.items
          : res?.data || [];
      setTransactions(rows);
    } catch (err) {
      console.error("Error fetching withdrawals:", err);
      setErrorMessage(err?.message || "Failed to fetch your withdrawals.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // initial fetches
    fetchPaymentMethods();
    fetchWithdrawals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasActiveWithdrawal = useMemo(
    () => transactions.some((t) => t.status === "pending" || t.status === "allowed"),
    [transactions]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccessMessage("");
    setError("");
    if (hasActiveWithdrawal) {
      setError("You already have an active withdrawal request.");
      return;
    }
    if (!user?.username) {
      setError("Missing username. Please sign in again.");
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        paymentMethod: selectedMethod,
        amount: parseFloat(amount),
        accountDetails,
        username: user.username, // as requested
      };
      const created = await withdrawalService.createWithdrawal(payload);

      // Optimistic UI: prepend new item or just refresh from server
      // We'll refresh to ensure server shape (reference, createdAt, etc.)
      await fetchWithdrawals();

      setSelectedMethod("");
      setAmount("");
      setAccountDetails("");
      setSuccessMessage("Withdrawal request submitted!");
    } catch (e) {
      console.error("Create withdrawal error:", e);
      setError(e?.response?.data?.message || e?.message || "Failed to submit withdrawal.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    setSuccessMessage("");
    setError("");
    try {
      await withdrawalService.cancelWithdrawal(id);
      await fetchWithdrawals();
      setSuccessMessage("Withdrawal cancelled.");
    } catch (e) {
      console.error("Cancel withdrawal error:", e);
      setError(e?.response?.data?.message || e?.message || "Failed to cancel withdrawal.");
    }
  };

  const selectedMethodDetails = paymentMethods.find((m) => m._id === selectedMethod);
  const isFormValid = selectedMethod && amount && parseFloat(amount) > 0 && accountDetails.trim().length > 0;

  if (loading && transactions.length === 0 && paymentMethods.length === 0) {
    return (
      <div className="text-white p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="text-white md:px-5 md:py-4 p-2 bg-[#000c24] md:bg-transparent max-w-screen-md mx-auto overflow-y-auto">
      <h1 className="md:text-[26px] text-[20px] font-semibold md:mb-6 mb-4 text-center md:text-left uppercase">
        Make a Withdrawal
      </h1>

      {errorMessage && (
        <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-6">
          {errorMessage}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-lg mb-6">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {hasActiveWithdrawal && (
        <div className="bg-yellow-500/20 border border-yellow-500 text-yellow-400 px-4 py-3 rounded-lg mb-6">
          ⚠️ You have an active withdrawal request. Please wait for it to be processed.
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 mb-8">
        <div className="flex flex-col w-full">
          <label className="text-gray-400 font-medium mb-2">
            Payment Method<span className="text-red-500 ml-1">*</span>
          </label>
          <select
            value={selectedMethod}
            onChange={(e) => setSelectedMethod(e.target.value)}
            required
            disabled={hasActiveWithdrawal || submitting}
            className="border border-gray-600 bg-[#000c24] text-white rounded-lg py-2 px-3"
          >
            <option value="">Select a payment method</option>
            {paymentMethods
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((m) => (
                <option key={m._id} value={m._id}>
                  {m.logo} {m.name}
                </option>
              ))}
          </select>
        </div>

        {selectedMethodDetails && (
          <div className="border border-gray-600 rounded-lg p-4 bg-gray-900">
            <div className="flex items-center space-x-3 mb-2">
              <span className="text-2xl">{selectedMethodDetails.logo}</span>
              <div>
                <h3 className="font-semibold text-white">{selectedMethodDetails.name}</h3>
                <p className="text-gray-400 text-sm">{selectedMethodDetails.description}</p>
              </div>
            </div>
          </div>
        )}

        {selectedMethod && (
          <>
            <div className="flex flex-col w-full">
              <label className="text-gray-400 font-medium mb-2">
                Montant<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Montant du retrait"
                min="1"
                step="0.01"
                required
                disabled={hasActiveWithdrawal || submitting}
                className="border border-gray-600 bg-[#000c24] text-white rounded-lg py-2 px-3"
              />
            </div>

            <div className="flex flex-col w-full">
              <label className="text-gray-400 font-medium mb-2">
                Account Details<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                value={accountDetails}
                onChange={(e) => setAccountDetails(e.target.value)}
                onKeyDown={(e) => {
                  // stop global listeners from blocking spacebar
                  if (e.key === " ") {
                    e.stopPropagation();
                    // some frameworks need this too:
                    if (e.nativeEvent?.stopImmediatePropagation) {
                      e.nativeEvent.stopImmediatePropagation();
                    }
                  }
                }}
                placeholder="Enter account details"
                required
                disabled={hasActiveWithdrawal || submitting}
                className="border border-gray-600 bg-[#000c24] text-white rounded-lg py-2 px-3"
              />
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={!isFormValid || hasActiveWithdrawal || submitting}
          className="bg-primary/80 text-gray-900 py-3 px-6 rounded-lg hover:bg-primary/70 transition w-full font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {hasActiveWithdrawal ? "ACTIVE REQUEST PENDING" : submitting ? "SUBMITTING..." : "SUBMIT WITHDRAWAL"}
        </button>
      </form>

      <TransactionTable
        title="Withdrawal History"
        transactions={transactions}
        paymentMethods={paymentMethods}
        showActions
        onCancel={handleCancel}
      />
    </div>
  );
}

export default UserWithdrawal;
